import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaCheck, FaSearch, FaFilter, FaExclamationCircle, FaChevronDown, FaChevronUp, FaTasks } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CrmTaskService, { CrmTask, CrmTaskStatus, CrmTaskPriority } from '../../sales/services/CrmTaskService';
import BusinessPartnerService, { BusinessPartner } from '../../contacts/services/BusinessPartnerService';
import EmployeeService from '../../employees/services/EmployeeService';
import { Employee } from '../../../hooks/useEmployeeData';
import CrmTaskModal from '../../sales/components/CrmTaskModal';
import QuickTaskInput from '../../sales/components/QuickTaskInput';
import { pdebugError } from '../utils/plantafelDebug';

type TabType = 'open' | 'done' | 'all';

export default function PlantafelTasksTab(): React.JSX.Element {
  const [tasks, setTasks] = useState<CrmTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('open');

  const [searchQuery, setSearchQuery] = useState('');
  const [filterPartnerId, setFilterPartnerId] = useState<number | null>(null);
  const [filterAssigneeId, setFilterAssigneeId] = useState<number | null>(null);
  const [filterDueFrom, setFilterDueFrom] = useState('');
  const [filterDueTo, setFilterDueTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [partners, setPartners] = useState<BusinessPartner[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<CrmTask | null>(null);
  const [completingTaskId, setCompletingTaskId] = useState<number | null>(null);

  const getStatusFilter = (): string | undefined => {
    switch (activeTab) {
      case 'open':
        return 'OPEN,IN_PROGRESS';
      case 'done':
        return 'DONE,CANCELLED';
      default:
        return undefined;
    }
  };

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await CrmTaskService.list({
        status: getStatusFilter(),
        search: searchQuery || undefined,
        partner_id: filterPartnerId || undefined,
        due_from: filterDueFrom || undefined,
        due_to: filterDueTo || undefined,
      });

      let filtered = data;
      if (filterAssigneeId) {
        filtered = data.filter((t) => t.assigned_to === filterAssigneeId);
      }

      filtered.sort((a, b) => {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      });

      setTasks(filtered);
    } catch (err) {
      pdebugError('Failed to load tasks:', err);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchQuery, filterPartnerId, filterAssigneeId, filterDueFrom, filterDueTo]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const [partnersData, employeesData] = await Promise.all([
        BusinessPartnerService.list({ kind: 'CUSTOMER' }),
        EmployeeService.fetchEmployeesByStatus(['ACTIVE']),
      ]);
      setPartners(partnersData);
      setEmployees(employeesData);
    } catch (err) {
      pdebugError('Failed to load filter options:', err);
    }
  }, []);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleCompleteTask = async (taskId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCompletingTaskId(taskId);
    try {
      await CrmTaskService.changeStatus(taskId, 'DONE');
      toast.success('Task completed');
      loadTasks();
    } catch (err) {
      pdebugError('Failed to complete task:', err);
      toast.error('Failed to complete task');
    } finally {
      setCompletingTaskId(null);
    }
  };

  const handleReopenTask = async (taskId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCompletingTaskId(taskId);
    try {
      await CrmTaskService.changeStatus(taskId, 'OPEN');
      toast.success('Task reopened');
      loadTasks();
    } catch (err) {
      pdebugError('Failed to reopen task:', err);
      toast.error('Failed to reopen task');
    } finally {
      setCompletingTaskId(null);
    }
  };

  const handleTaskClick = (task: CrmTask) => {
    setEditingTask(task);
    setShowTaskModal(true);
  };

  const handleNewTask = () => {
    setEditingTask(null);
    setShowTaskModal(true);
  };

  const handleTaskSaved = () => {
    setShowTaskModal(false);
    setEditingTask(null);
    loadTasks();
  };

  const handleQuickTaskCreated = () => {
    loadTasks();
  };

  const getPartnerDisplayName = (partner: BusinessPartner): string => {
    if (partner.is_company && partner.company_name) {
      return partner.company_name;
    }
    return `${partner.first_name || ''} ${partner.last_name || ''}`.trim() || `Partner #${partner.id}`;
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterPartnerId(null);
    setFilterAssigneeId(null);
    setFilterDueFrom('');
    setFilterDueTo('');
  };

  const hasActiveFilters = searchQuery || filterPartnerId || filterAssigneeId || filterDueFrom || filterDueTo;

  const getPriorityBadge = (priority: CrmTaskPriority) => {
    const colors = CrmTaskService.getPriorityColor(priority);
    return (
      <span
        style={{
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: 600,
          background: colors.bg,
          color: colors.text,
        }}
      >
        {priority}
      </span>
    );
  };

  const getStatusBadge = (status: CrmTaskStatus) => {
    const colors = CrmTaskService.getStatusColor(status);
    return (
      <span
        style={{
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: 600,
          background: colors.bg,
          color: colors.text,
        }}
      >
        {CrmTaskService.getStatusLabel(status)}
      </span>
    );
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto', height: '100%', overflow: 'auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span
            style={{
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: 600,
              background: '#e0e7ff',
              color: '#3730a3',
            }}
          >
            {tasks.length} Tasks
          </span>
        </div>
        <button
          onClick={handleNewTask}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '8px',
            background: '#7c3aed',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <FaPlus size={12} />
          New task
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '16px',
          borderBottom: '1px solid #e0e3ef',
          paddingBottom: '0',
        }}
      >
        {(['open', 'done', 'all'] as TabType[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'none',
              fontSize: '14px',
              fontWeight: 600,
              color: activeTab === tab ? '#7c3aed' : '#666',
              cursor: 'pointer',
              borderBottom: activeTab === tab ? '2px solid #7c3aed' : '2px solid transparent',
              marginBottom: '-1px',
              textTransform: 'capitalize',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e0e3ef',
          marginBottom: '16px',
          overflow: 'hidden',
        }}
      >
        <QuickTaskInput onTaskCreated={handleQuickTaskCreated} placeholder="Add task..." />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 20px',
            borderTop: '1px solid #e0e3ef',
          }}
        >
          <div style={{ position: 'relative', flex: 1, maxWidth: '300px' }}>
            <FaSearch
              size={14}
              color="#9ca3af"
              style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tasks..."
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              border: '1px solid #e0e3ef',
              borderRadius: '6px',
              background: hasActiveFilters ? '#f0fdf4' : 'white',
              color: hasActiveFilters ? '#16a34a' : '#666',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <FaFilter size={12} />
            Filters
            {hasActiveFilters && <span style={{ fontWeight: 700 }}>*</span>}
            {showFilters ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
          </button>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                padding: '8px 14px',
                border: 'none',
                borderRadius: '6px',
                background: '#fee2e2',
                color: '#dc2626',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '12px',
              padding: '16px 20px',
              borderTop: '1px solid #e0e3ef',
              background: '#fafbfc',
            }}
          >
            <div>
              <label style={filterLabelStyle}>Customer</label>
              <select
                value={filterPartnerId || ''}
                onChange={(e) => setFilterPartnerId(e.target.value ? Number(e.target.value) : null)}
                style={filterSelectStyle}
              >
                <option value="">All customers</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {getPartnerDisplayName(p)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={filterLabelStyle}>Assignee</label>
              <select
                value={filterAssigneeId || ''}
                onChange={(e) => setFilterAssigneeId(e.target.value ? Number(e.target.value) : null)}
                style={filterSelectStyle}
              >
                <option value="">All assignees</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={filterLabelStyle}>Due from</label>
              <input
                type="date"
                value={filterDueFrom}
                onChange={(e) => setFilterDueFrom(e.target.value)}
                style={filterSelectStyle}
              />
            </div>
            <div>
              <label style={filterLabelStyle}>Due to</label>
              <input
                type="date"
                value={filterDueTo}
                onChange={(e) => setFilterDueTo(e.target.value)}
                style={filterSelectStyle}
              />
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e0e3ef',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#888' }}>Loading...</div>
        ) : tasks.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#888' }}>
            <FaTasks size={32} color="#e0e3ef" style={{ marginBottom: '12px' }} />
            <p style={{ margin: 0, fontSize: '14px' }}>No tasks found</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#aaa' }}>
              {hasActiveFilters ? 'Try adjusting your filters' : 'Create your first task to get started'}
            </p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e0e3ef', background: '#fafbfc' }}>
                <th style={thStyle}></th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Title</th>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Project</th>
                <th style={thStyle}>Assignee</th>
                <th style={thStyle}>Due date</th>
                <th style={thStyle}>Priority</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => {
                const isOverdue = CrmTaskService.isOverdue(task.due_date) && task.status !== 'DONE';
                const isDone = task.status === 'DONE' || task.status === 'CANCELLED';

                return (
                  <tr
                    key={task.id}
                    onClick={() => handleTaskClick(task)}
                    style={{
                      borderBottom: '1px solid #f0f1f5',
                      cursor: 'pointer',
                      background: isOverdue ? '#fef2f2' : 'white',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isOverdue) e.currentTarget.style.background = '#fafbfc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isOverdue ? '#fef2f2' : 'white';
                    }}
                  >
                    <td style={{ ...tdStyle, width: '50px', textAlign: 'center' }}>
                      {isDone ? (
                        <button
                          onClick={(e) => handleReopenTask(task.id, e)}
                          disabled={completingTaskId === task.id}
                          style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            border: 'none',
                            background: '#10b981',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title="Reopen task"
                        >
                          <FaCheck size={10} />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => handleCompleteTask(task.id, e)}
                          disabled={completingTaskId === task.id}
                          style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            border: '2px solid #d1d5db',
                            background: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title="Mark as done"
                        >
                          {completingTaskId === task.id && <span style={{ fontSize: '10px' }}>...</span>}
                        </button>
                      )}
                    </td>

                    <td style={{ ...tdStyle, textAlign: 'left' }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: isDone ? '#9ca3af' : '#021a4a',
                          textDecoration: isDone ? 'line-through' : 'none',
                        }}
                      >
                        {task.title}
                      </div>
                      {task.description && (
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#9ca3af',
                            marginTop: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '300px',
                          }}
                        >
                          {task.description}
                        </div>
                      )}
                    </td>

                    <td style={tdStyle}>
                      <span style={{ color: task.partner_name ? '#021a4a' : '#9ca3af', fontSize: '13px' }}>
                        {task.partner_name || '-'}
                      </span>
                    </td>

                    <td style={tdStyle}>
                      <span style={{ color: task.project_name ? '#021a4a' : '#9ca3af', fontSize: '13px' }}>
                        {task.project_name || '-'}
                      </span>
                    </td>

                    <td style={tdStyle}>
                      {task.assigned_to ? (
                        <span style={{ fontSize: '13px', color: '#021a4a' }}>
                          {employees.find((e) => e.id === task.assigned_to)?.first_name || 'Assigned'}
                        </span>
                      ) : (
                        <span style={{ fontSize: '13px', color: '#9ca3af' }}>-</span>
                      )}
                    </td>

                    <td style={tdStyle}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          justifyContent: 'center',
                        }}
                      >
                        {isOverdue && <FaExclamationCircle size={12} color="#dc2626" />}
                        <span
                          style={{
                            fontSize: '13px',
                            color: isOverdue ? '#dc2626' : '#021a4a',
                            fontWeight: isOverdue ? 600 : 400,
                          }}
                        >
                          {CrmTaskService.formatDueDate(task.due_date)}
                        </span>
                      </div>
                    </td>

                    <td style={tdStyle}>{getPriorityBadge(task.priority)}</td>

                    <td style={tdStyle}>{getStatusBadge(task.status)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <CrmTaskModal
        isOpen={showTaskModal}
        task={editingTask}
        onClose={() => {
          setShowTaskModal(false);
          setEditingTask(null);
        }}
        onSaved={handleTaskSaved}
      />
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '11px',
  fontWeight: 600,
  color: '#666',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  textAlign: 'center',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '14px',
  textAlign: 'center',
};

const filterLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 600,
  color: '#666',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  marginBottom: '4px',
};

const filterSelectStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '13px',
  background: 'white',
  cursor: 'pointer',
};
